/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, HostListener, Inject, OnDestroy, OnInit } from "@angular/core";
import { filter, Subject, switchMap, take, takeUntil } from "rxjs";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Store } from "@ngrx/store";
import { BehaviorSubject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileInfo, OwnCloudService } from "../../owncloud.service";
import { RootState } from "src/app/reducers";
import { environment } from "src/environments/environment";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { ConfigService } from "src/app/config.service";
import { MailService } from "src/app/mail/shared/services/mail-service";
import { CommonUtils } from "src/app/common/utils/common-util";
import { MailConstants } from "src/app/common/utils/mail-constants";
import moment from "moment";

@Component({
  selector: "vp-owncloud-detail",
  styleUrls: ["./owncloud-detail.scss"],
  templateUrl: "./owncloud-detail.html"
})
export class OwncloudDetailDialogComponent implements OnInit, OnDestroy {
  files$ = new BehaviorSubject<FileInfo[]>([]);
  currentFolder: any;
  file: any;
  isLoading: boolean;
  lang = localStorage.getItem("portalLanguage") || "en";
  private isAlive$ = new Subject<boolean>();
  isPending: any;
  newFolder: any;
  defaultAvatar = "";
  isNextCloud: boolean = false;
  showAddButton: boolean = false;
  constructor(
    private store: Store<RootState>,
    private ownCloudService: OwnCloudService,
    private configService: ConfigService,
    private dialog: MatDialog,
    private mailService: MailService,
    private dialogRef: MatDialogRef<OwncloudDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.file = this.data.file;
    if (this.file && typeof this.file?.permissions === "string") {
      if (this.file.permissions?.includes("R")) {
        this.showAddButton = true;
      }
    }
  }

  ngOnInit() {
    this.isNextCloud = this.configService.get("isNextCloud");
    this.defaultAvatar = CommonUtils.getFullUrl("/assets/icon/groupchat.svg");
    this.ownCloudService.isLoggedIn().pipe(filter(v => !!v), take(1)
    , switchMap(() => {
      this.isLoading = true;
      return this.ownCloudService.getFilesSharing(this.file.path);
    })).subscribe(v => {
      this.isLoading = false;
      this.files$.next(v.filter(v => !!v.name));
    });

    this.configService.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe((lang: string) => {
      this.lang = lang;
    });
    if (environment.isCordova) {
      StatusBar.backgroundColorByHexString("#000000");
      StatusBar.styleBlackTranslucent();
    }
  }

  copyToClipboard(file) {
    this.mailService.openSnackBarWithTranslation("COPIED_TO_CLIPBOARD");
    MailUtils.copyToClipboard([file.sharedUrl]);
  }

  delete(file) {
    this.ownCloudService.deleteSharedFile(file.id).subscribe(() => {
      this.files$.next(this.files$.value.filter(v => v.id !== file.id));
      if (this.files$.value.length === 0) {
        this.ownCloudService.removeSharedFlag$.next(file.fileId.toString());
      }
      this.mailService.openSnackBarWithTranslation("SHARE_DELETED_SUCCESSFULLY");
    });
  }

  ngOnDestroy() {
    this.isAlive$.next(false);
    this.isAlive$.complete();
    if (document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "none";
    }
    if (environment.isCordova) {
      StatusBar.backgroundColorByHexString("#317bbc");
    }
  }

  @HostListener("document:keydown.esc")
  cancel(): void {
    this.dialogRef.close();
  }

  get isMobileSize () {
    return MailUtils.isMobileSize();
  }

  async edit(file) {
    let style: any = {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
    };
    if (MailUtils.isMobileSize()) {
      style = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
    }
    if (file.shareTypes === 3) {
      const { OwncloudPublicDialogComponent } = await import(
        "src/app/shared/components/owncloud-public/owncloud-public.component");
      this.dialog.open(OwncloudPublicDialogComponent, Object.assign({
        width: "480px",
        maxWidth: "95%",
        height: "610px",
        backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
        panelClass: "common-dialog-panel",
        disableClose: true,
        data: {
          file: file,
          isEdit: true
        },
        autoFocus: true
      }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
        if (data && data.shared) {
          let expireDate = "";
          if (data.expirationDate) {
            expireDate = data.expirationDate.format("dd-MM-yyyy");
          }
          this.ownCloudService.updateSharedFile(file.id, { expireDate: expireDate, permissions: data.permission, shareType: 3, password: data.password, name: data.name }).subscribe(v => {
            this.mailService.openSnackBarWithTranslation("UPDATED");
            file.sharedName = v.sharedName;
            file.expiration = v.expiration;
            file.permissions = v.permissions;
            const newFiles = this.files$.value;
            newFiles.splice(newFiles.findIndex(v => v.id === file.id), 1, file);
            this.files$.next(newFiles);
          });
        } else if (data && data.deleted) {
          this.delete(file);
        } else if (data && data.copied) {
          this.copyToClipboard(file);
        }
      });
    } else {

      const { OwncloudShareDialogComponent } = await import(
        "src/app/shared/components/owncloud-share/owncloud-share.component");
      this.dialog.open(OwncloudShareDialogComponent, Object.assign({
        backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
        panelClass: "common-dialog-panel",
        disableClose: true,
        data: {
          file: file
        },
        autoFocus: true
      }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
        if (data && !!data.shared && !!data.users && (data.users.length > 0)) {
          data.users.forEach(sharee => {
            const payload = {
              format: "json",
              permissions: parseInt(data.permission, 10),
              password: (sharee.shareType === 4) ? MailUtils.randomId(10, true) : null
            };
            this.ownCloudService.updateShareFile(file.id, payload).pipe(take(1)).subscribe(v => {
              this.mailService.openSnackBarWithTranslation("UPDATED");
              this.ownCloudService.getFilesSharing(this.file.path).pipe(take(1)).subscribe(v => {
                this.files$.next(v.filter(v => !!v.name));
              });
            }, err => {
              this.mailService.openSnackBarWithTranslation("CANNOT_SHARE");
              console.log("[sharePublicLink] shareFileWithLink err", err);
            });
          });
        }
      });
    }
  }

  async shareWithUsers(file) {
    let style: any = {
      width: "480px",
      maxWidth: "95%",
      height: "610px",
    };
    if (MailUtils.isMobileSize()) {
      style = {
        width: "100vw",
        maxWidth: "100vw",
        height: "100vh"
      };
    }
      const { OwncloudShareDialogComponent } = await import(
      "src/app/shared/components/owncloud-share/owncloud-share.component");
      this.dialog.open(OwncloudShareDialogComponent, Object.assign({
        backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
        panelClass: "common-dialog-panel",
        disableClose: true,
        data: {
          file: file
        },
        autoFocus: true
      }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
        if (data && !!data.shared && !!data.users && (data.users.length > 0)) {
          data.users.forEach(sharee => {
            const payload = {
              format: "json",
              permissions: parseInt(data.permission, 10),
              shareType: sharee.shareType,
              shareWith: sharee.shareWith,
              label: sharee.fullName,
              password: (sharee.shareType === 4) ? MailUtils.randomId(10, true) : null
            };
            this.ownCloudService.shareFileWithLink(file.path, payload).pipe(take(1)).subscribe(v => {
              this.mailService.openSnackBarWithTranslation("SHARED");
              this.getFiles(true, file);
            }, err => {
              this.mailService.openSnackBarWithTranslation("CANNOT_SHARE");
            });
          });
        }
      });
    }

    async sharePublicLink(file, data) {
      let style: any = {
        width: "480px",
        maxWidth: "95%",
        height: "610px",
      };
      if (MailUtils.isMobileSize()) {
        style = {
          width: "100vw",
          maxWidth: "100vw",
          height: "100vh"
        };
      }
      const { OwncloudPublicDialogComponent } = await import(
        "src/app/shared/components/owncloud-public/owncloud-public.component");
      this.dialog.open(OwncloudPublicDialogComponent, Object.assign({
        width: "480px",
        maxWidth: "95%",
        height: "610px",
        backdropClass: MailUtils.isMobileSize() ? "common-dialog-backdrop-no-border" : "common-dialog-backdrop",
        panelClass: "common-dialog-panel",
        disableClose: true,
        data: {
          file: file,
          data: data
        },
        autoFocus: true
      }, style)).afterClosed().pipe(take(1)).subscribe((data) => {
        if (data && data.shared) {
          let expireDate = "";
          if (data.expirationDate) {
            expireDate = moment(data.expirationDate).format("DD-MM-YYYY");
          }
          let shareData = {
            expireDate: expireDate,
            format: "json",
            permissions: data.permission,
            shareType: 3,
            password: data.password,
            name: data.name
          };
          if (this.isNextCloud) {
            shareData["label"] = data.name;
            this.ownCloudService.shareFileWithLink(file.path, shareData).pipe(take(1)).subscribe(res => {
              if (!!res && (res.length > 0)) {
                this.mailService.openSnackBarWithTranslation("COPIED_TO_CLIPBOARD");
                MailUtils.copyToClipboard([res]);
                this.getFiles(true, file);
              }
            }, err => {
              if (err === MailConstants.OWNCLOUD_CANNOT_SHARE_PASSWORD) {
                this.mailService.openSnackBarWithTranslation("CANNOT_SHARE_PASSWORD", 10000);
              } else {
                this.mailService.openSnackBarWithTranslation("CANNOT_SHARE", 10000);
              }
              shareData["expirationDate"] = data.expirationDate;
              this.sharePublicLink(file, shareData);
            });
          } else {
            this.ownCloudService.oc.shares.shareFileWithLink(file.path, shareData).then(v => {
              if (v && v.shareInfo) {
                this.mailService.openSnackBarWithTranslation("COPIED_TO_CLIPBOARD");
                MailUtils.copyToClipboard([v.shareInfo.url]);
                this.getFiles(true, file);
              }
            }).catch(err => {
              this.mailService.openSnackBarWithTranslation("CANNOT_SHARE");
            });
          }
        }
      });
    }

  getFiles(update?: boolean, file?: any) {
    this.ownCloudService.isLoggedIn().pipe(filter(v => !!v), take(1)
    , switchMap(() => {
      this.isLoading = true;
      return this.ownCloudService.getFilesSharing(this.file.path);
    })).subscribe(v => {
      this.isLoading = false;
      this.files$.next(v.filter(v => !!v.name));
      if (update) {
        this.ownCloudService.sharedReload$.next(file);
      }
    });
  }
}
